import {
  Card,
  CardContent,
  Container,
  Grid,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CardSpacer from "@saleor/components/CardSpacer";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { configurationOpsMenuUrl } from "@saleor/configuration_operations";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  useShippingRateCreate,
  useShippingRateUpdate
} from "@saleor/shipping/mutations";
import { useDistancePrices, useShippingZone } from "@saleor/shipping/queries";
import {
  shippingZonesListPath,
  shippingZoneUrl,
  ShippingZoneUrlQueryParams
} from "@saleor/shipping/urls";
import {
  getCreateShippingLocationRateVariables,
  getUpdateShippingLocationRateVariables
} from "@saleor/shipping/views/ShippingZoneDetails/data";
import {
  DeliveryTypeEnum,
  DistancePricesInput,
  ShippingMethodStatus,
  ShippingMethodStatusEnum,
  ShippingMethodTypeEnum
} from "@saleor/types/globalTypes";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import LocationRateTableInput from "./LocationRateTableInput";
import {
  LOCATION_RATE_DELIVERY_OPTIONS,
  validateLocationRateMax
} from "./utils";

const useStyles = makeStyles(
  theme => ({
    cardContainer: {
      display: "grid",
      gridColumnGap: theme.spacing(3),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("sm")]: {
        gridColumnGap: theme.spacing(1)
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    cardTitle: {
      "& span": {
        fontWeight: 600
      },
      margin: "0 0 20px 0"
    },
    pageHeader: {
      display: "flex",
      flexDirection: "row",
      marginTop: "10px"
    }
  }),
  { name: "LocationRate" }
);

export interface LocationRateRowObject {
  index: number;
  disableMax: boolean;
  disableMin: boolean;
  disablePrice: boolean;
  hideDelete: boolean;
  max: string;
  min: string;
  price: string;
  maxError: string;
  minError: string;
  feeError: string;
}

export interface LocationRateProps {
  shippingZoneId: string;
  locationId?: string;
  params: ShippingZoneUrlQueryParams;
}

const LocationRate: React.FC<LocationRateProps> = props => {
  const { locationId, shippingZoneId } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const notify = useNotifier();
  const navigate = useNavigator();

  const [active, setActive] = useState(true);
  const [rateName, setRateName] = useState("");
  const [delType, setDelType] = useState("");
  const [defaultDelType, setDefaultDelType] = useState("");
  const [defaultRows, setDefaultRows] = useState<LocationRateRowObject[]>([]);
  const [distancePrices, setDistancePrices] = useState<DistancePricesInput[]>(
    []
  );
  const [rows, setRows] = useState<LocationRateRowObject[]>([
    {
      disableMax: false,
      disableMin: false,
      disablePrice: false,
      feeError: "",
      hideDelete: true,
      index: 0,
      max: "0.00",
      maxError: "",
      min: "0.00",
      minError: "",
      price: "0.00"
    }
  ]);
  const [editState, setEditState] = useState(null);
  const [formIsUpdated, setFormIsUpdated] = useState(true);

  const { data: shippingZoneData } = useShippingZone({
    displayLoader: true,
    skip: !shippingZoneId || shippingZoneId.length === 0,
    variables: { id: shippingZoneId }
  });

  const { data } = useDistancePrices({
    displayLoader: true,
    skip: !locationId || locationId.length === 0,
    variables: { shippingMethodIds: [locationId] }
  });

  React.useEffect(() => {
    if (
      data?.distancePrices?.edges &&
      data?.distancePrices?.edges?.length > 0
    ) {
      const defaultDistancePrices =
        data?.distancePrices?.edges?.map(e => e?.node) || [];
      const latestState = {
        active:
          (data?.distancePrices?.edges?.[0]?.node?.shippingmethodId?.status ??
            ShippingMethodStatus.INACTIVE) === ShippingMethodStatus.ACT,
        delType:
          data?.distancePrices?.edges?.[0]?.node?.shippingmethodId
            ?.deliveryType ?? "",
        editMode: !!locationId,
        rateName:
          data?.distancePrices?.edges?.[0]?.node?.shippingmethodId?.name ?? "",
        rows: defaultDistancePrices.map((dp, i) => {
          const isLastRow = i + 1 === defaultDistancePrices.length;
          return {
            disableMax: defaultDistancePrices.length > 1 && !isLastRow,
            disableMin: true,
            disablePrice: false,
            feeError: "",
            hideDelete: !isLastRow || i === 0,
            index: i,
            max: parseFloat(dp?.toKm.toString() || "0").toFixed(2),
            maxError: "",
            min: parseFloat(dp?.fromKm.toString() || "0").toFixed(2),
            minError: "",
            price: parseFloat(dp?.price?.amount.toString() || "0").toFixed(2)
          };
        }),
        status:
          data?.distancePrices?.edges?.[0]?.node?.shippingmethodId?.status ??
          ShippingMethodStatus.INACTIVE
      };
      setEditState(latestState);

      setDefaultRows(latestState.rows);
      setRateName(latestState.rateName);
      setDelType(latestState.delType);
      setDefaultDelType(latestState.delType);
      setActive(latestState.status === ShippingMethodStatus.ACT);
    }
  }, [data]);

  const actionTitle = editState?.editMode ? "Edit" : "Create";
  const breadcrumbDirectory = [
    {
      name: "Configuration",
      url: configurationOpsMenuUrl
    },
    {
      name: "Shipping Methods",
      url: shippingZonesListPath
    },
    {
      name: actionTitle + " Location Range Rate"
    }
  ];

  const rowHasError = rows.some(r => r.maxError || r.minError || r.feeError);
  const disableSave =
    !rateName || !delType || rows.length === 0 || rowHasError || !formIsUpdated;
  const activeLocationRates =
    !!shippingZoneData && !!shippingZoneData?.shippingZone
      ? shippingZoneData?.shippingZone?.shippingMethods?.filter(
          r =>
            r.type === ShippingMethodTypeEnum.LOCATION &&
            r.status === ShippingMethodStatus.ACT
        )
      : [];
  const disableSetAsActive =
    activeLocationRates.length === 1 &&
    activeLocationRates[0]?.id === locationId;

  React.useEffect(() => {
    if (rows.length) {
      setDistancePrices(
        rows.map(row => ({
          fromKm: parseFloat(row.min) || 0,
          price: parseFloat(row.price) || 0,
          toKm: parseFloat(row.max) || 0
        }))
      );
    }
  }, [rows]);

  const [createShippingRate] = useShippingRateCreate({
    onCompleted: data => {
      if (data.shippingPriceCreate.errors.length === 0) {
        notify({
          status: "success",
          text:
            data?.shippingPriceCreate?.shippingMethod?.name +
            " Location Range Rate has been created successfully."
        });
        onBack();
      } else if (data.shippingPriceCreate.errors.length > 0) {
        data.shippingPriceCreate.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    }
  });

  const [updateShippingRate] = useShippingRateUpdate({
    onCompleted: data => {
      if (data.shippingPriceUpdate.errors.length === 0) {
        notify({
          status: "success",
          text:
            data?.shippingPriceUpdate?.shippingMethod?.name +
            " Location Range Rate has been updated successfully."
        });
        onBack();
      } else if (data.shippingPriceUpdate.errors.length > 0) {
        data.shippingPriceUpdate.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    }
  });

  const onBack = () => {
    navigate(shippingZoneUrl(shippingZoneId));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActive(event.target.checked);
  };

  const handleSave = () => {
    const maxForDelType = parseFloat(
      LOCATION_RATE_DELIVERY_OPTIONS.find(d => d.value === delType)?.max ||
        "0.00"
    );
    if (
      !validateLocationRateMax(
        rows[rows.length - 1],
        setRows,
        maxForDelType,
        parseFloat(rows[rows.length - 1].max).toFixed(2)
      )
    ) {
      return;
    }

    if (editState?.editMode) {
      updateShippingRate({
        variables: getUpdateShippingLocationRateVariables(
          Object.values(DeliveryTypeEnum).includes(delType as DeliveryTypeEnum)
            ? (delType as DeliveryTypeEnum)
            : null,
          distancePrices,
          rateName,
          active
            ? ShippingMethodStatusEnum.ACT
            : ShippingMethodStatusEnum.INACTIVE,
          locationId
        )
      });
    } else {
      createShippingRate({
        variables: getCreateShippingLocationRateVariables(
          Object.values(DeliveryTypeEnum).includes(delType as DeliveryTypeEnum)
            ? (delType as DeliveryTypeEnum)
            : null,
          distancePrices,
          rateName,
          active
            ? ShippingMethodStatusEnum.ACT
            : ShippingMethodStatusEnum.INACTIVE,
          shippingZoneId
        )
      });
    }
  };

  React.useEffect(() => {
    if (editState && editState?.editMode) {
      const hasRateNameChanged = rateName !== editState?.rateName;
      const hasDelTypeChanged = delType !== editState?.delType;
      const hasActiveChanged = active !== editState?.active;
      const haveRowsChanged =
        JSON.stringify(rows) !== JSON.stringify(editState?.rows);

      if (
        !formIsUpdated &&
        (hasRateNameChanged ||
          hasDelTypeChanged ||
          hasActiveChanged ||
          haveRowsChanged)
      ) {
        setFormIsUpdated(true);
      } else if (
        formIsUpdated &&
        !hasRateNameChanged &&
        !hasDelTypeChanged &&
        !hasActiveChanged &&
        !haveRowsChanged
      ) {
        setFormIsUpdated(false);
      }
    }
  }, [rateName, delType, active, rows, editState, formIsUpdated]);

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div className={classes.pageHeader}>
        <PageBackNavigation onBack={() => onBack()} />
        <PageHeader title={actionTitle + " Location Range Rate"}></PageHeader>
      </div>
      <Card>
        <CardContent>
          <Grid>
            <div className={classes.cardContainer}>
              <TextField
                id="shippingLocationRateName"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Rate Name",
                  description: "shipping method name"
                })}
                value={rateName}
                onChange={e => {
                  setRateName(e.target.value);
                }}
              />
              <SingleSelectField
                name="shippingLocationRateDelType"
                label={intl.formatMessage({
                  defaultMessage: "Delivery Type"
                })}
                choices={LOCATION_RATE_DELIVERY_OPTIONS}
                value={delType}
                onChange={e => {
                  setDelType(e.target.value);
                }}
                disabled={editState?.editMode}
              />
            </div>
          </Grid>
          <CardSpacer />
          <Grid>
            <Card>
              <CardContent>
                <h1 className={classes.cardTitle}>Add Distance and Pricing</h1>
                <LocationRateTableInput
                  defaultDelType={defaultDelType}
                  selectedDelType={delType}
                  defaultRows={defaultRows}
                  rowHasError={rowHasError}
                  rows={rows}
                  setRows={setRows}
                />
              </CardContent>
            </Card>
          </Grid>
        </CardContent>
      </Card>
      <SaveButtonBar
        onCancel={() => onBack()}
        onSave={() => handleSave()}
        saveButtonVariant={true}
        disabled={disableSave}
        disableAlternateButton={disableSave}
        state={"default"}
        checkboxState={{
          checked: active,
          disabled: disableSetAsActive,
          label: "Set as the active rule for this delivery type.",
          onChange: handleCheckboxChange
        }}
      />
    </Container>
  );
};

LocationRate.displayName = "LocationRate";
export default LocationRate;
